import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingBag, Sparkles, UtensilsCrossed } from "lucide-react";
import { readConsumerRuntimeDeviceMode, readConsumerRuntimeTableCode } from "../runtime-device-mode";
import { trn, useI18n } from "../shared/i18n";

const OrderType = () => {
  const navigate = useNavigate();
  const { language } = useI18n();
  void language;
  const isWide = typeof window !== "undefined" && window.innerWidth > 900;
  const deviceMode = readConsumerRuntimeDeviceMode();
  const tableCode = readConsumerRuntimeTableCode();

  useEffect(() => {
    if (deviceMode === "table") {
      navigate(`/menu?type=eat-in${tableCode ? `&table=${encodeURIComponent(tableCode)}` : ""}`, { replace: true });
    }
  }, [deviceMode, navigate, tableCode]);

  if (deviceMode === "table") return null;

  return (
    <div className="fullscreen-container-dark">
      <div
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          zIndex: 3,
        }}
      >
        <button
          type="button"
          className="btn btn-outline-light d-flex align-items-center gap-2"
          style={{
            borderRadius: "999px",
            padding: "12px 18px",
            fontWeight: 700,
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.25)",
          }}
          onClick={() => navigate("/", { replace: true })}
        >
          <ArrowLeft size={18} />
          {trn(
            "app.consumer.order_type.back_home",
            "Back to start"
          )}
        </button>
      </div>
      <div className="fullscreen-content">
        <div className="order-type-shell">
          <div className="order-type-hero">
            <div className="order-type-kicker">
              <Sparkles size={18} />
              <span>{trn("app.consumer.order_type.kicker", "Ready to start")}</span>
            </div>
            <h1 className="order-type-title">
              {trn(
                "app.consumer.order_type.title",
                "Is this order to take away or to eat here?",
                null,
                "Main question on a large self-service restaurant kiosk screen in a restaurant. Ask the guest to choose whether this order is to take away or to eat here. Keep it short, natural, and customer-facing. For Spanish, prefer an idiomatic phrasing equivalent to: '¿Pides para llevar o para comer aquí?'. For Catalan, prefer a natural phrasing equivalent to: 'Vols per emportar o per menjar aquí?'."
              )}
            </h1>
          </div>

          <div
            className="order-type-grid"
            style={{
              flexDirection: isWide ? "row" : "column",
            }}
          >
          <button
            type="button"
            className="order-type-card"
            style={{ width: isWide ? "45%" : "100%" }}
            onClick={() => navigate("/menu?type=takeaway")}
          >
            <div className="order-type-card__halo" />
            <div className="order-type-card__icon">
              <ShoppingBag size={56} strokeWidth={2.1} />
            </div>
            <div className="order-type-card__label">
              {trn(
                "app.consumer.order_type.takeaway",
                "To go",
                null,
                "Short label for the takeaway option on a restaurant self-service kiosk. Prefer the most natural customer-facing phrasing used on food ordering screens."
              )}
            </div>
            <span className="order-type-card__description">
              {trn(
                "app.consumer.order_type.takeaway_hint",
                "Choose this if you want to take your food with you.",
                null,
                "Helper text under the takeaway option on a restaurant kiosk. Explain clearly that this option is for customers who will not eat at the restaurant."
              )}
            </span>
            <div className="order-type-card__cta">
              {trn(
                "app.consumer.order_type.takeaway_cta",
                "Order to go",
                null,
                "Short CTA-style label under the takeaway option on a self-service restaurant kiosk."
              )}
            </div>
          </button>

          <button
            type="button"
            className="order-type-card"
            style={{ width: isWide ? "45%" : "100%" }}
            onClick={() => navigate("/menu?type=eat-in")}
          >
            <div className="order-type-card__halo" />
            <div className="order-type-card__icon">
              <UtensilsCrossed size={56} strokeWidth={2.1} />
            </div>
            <div className="order-type-card__label">
              {trn(
                "app.consumer.order_type.eat_in",
                "Eat here",
                null,
                "Short label for the dine-in option on a restaurant self-service kiosk."
              )}
            </div>
            <span className="order-type-card__description">
              {trn(
                "app.consumer.order_type.eat_in_hint",
                "Choose this if you will stay and eat at the restaurant.",
                null,
                "Helper text under the dine-in option on a restaurant kiosk. Explain clearly that this option is for customers eating on site."
              )}
            </span>
            <div className="order-type-card__cta">
              {trn(
                "app.consumer.order_type.eat_in_cta",
                "Order for here",
                null,
                "Short CTA-style label under the dine-in option on a self-service restaurant kiosk."
              )}
            </div>
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default OrderType;
